import { API_CONFIG } from '@/config/api.config';

const API_BASE = API_CONFIG.API_GATEWAY;
const TENANT_ID = API_CONFIG.TENANT_ID;

/**
 * Shared headers for ALL public requests
 */
function withTenant(headers: Record<string, string> = {}) {
    const defaultHeaders: Record<string, string> = {
        ...headers,
        "X-Tenant-Id": TENANT_ID
    };

    // [SHORTCUT] Automatically forward the real client IP if we are running on the Next.js server
    if (typeof window === 'undefined') {
        try {
            // Using require inside try/catch avoids breaking Client Components while still retrieving the IP in SSR
            const h = require('next/headers').headers();
            const clientIp = h.get('x-forwarded-for') || h.get('x-real-ip');
            if (clientIp) {
                defaultHeaders["X-Forwarded-For"] = clientIp;
            }
        } catch (e) {
            // Silently ignore if not in a server request context
        }
    }

    return defaultHeaders;
}

/**
 * Fetch Report Detail by Slug
 */
export async function fetchReportBySlug(slug: string) {
    const res = await fetch(
        `${API_BASE}/v1/public/reports/${slug}?include_related=true`,
        {
            cache: 'no-store',
            // The backend issues a 301 with a RELATIVE Location (`/report/<to-slug>`)
            // when `slug` has an active entry in content_redirect — that path is meant
            // for the site, not the API. `fetch`'s default redirect handling resolves a
            // relative Location against the API host (this request's own origin), so it
            // silently re-requests `${API_BASE}/report/<to-slug>` — a path that doesn't
            // exist on the API — and that 404s. 'manual' intercepts the 301 here so we
            // can hand the real site-relative path back to the caller instead.
            redirect: 'manual',
            headers: withTenant()
        }
    );

    if (res.status === 301 || res.status === 302) {
        const location = res.headers.get('location');
        if (location) {
            return { __redirect: location };
        }
        return null;
    }

    if (!res.ok) {
        throw new Error("Failed to fetch report");
    }

    const data = await res.json();

    // The backend returns HTTP 200 with `{ found: false, suggested_results: [...] }`
    // when the slug doesn't resolve (the "404-suggestion" contract). That payload has
    // no `title`/`metadata_json`, so treat it as not-found — callers' `if (!reportData)`
    // guards then render the "Report Not Found" page (SSR notFound() / CSR error state)
    // instead of trying to render a report and crashing. See GAP_TRACKER BUG-1.
    if (!data || data.found === false || !data.title) {
        return null;
    }

    return data;
}

/**
 * Homepage "In the Press" carousel (SSR): the admin-curated list, or the newest items when none is curated.
 * Backend: GET /v1/public/press-releases/featured (cached 60s per backend worker).
 * Returns null on any failure so the caller can fall back — the homepage must never break on this.
 */
export async function fetchFeaturedPress(limit = 9) {
    try {
        const res = await fetch(`${API_BASE}/v1/public/press-releases/featured?limit=${limit}`, {
            cache: 'no-store',
            headers: withTenant(),
            signal: AbortSignal.timeout(2500),
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (!Array.isArray(data?.items) || !data.items.length) return null;
        // metadata_json can arrive as a JSON string for older rows (same guard as InThePressContent)
        return data.items.map((i: any) => {
            if (typeof i.metadata_json !== 'string') return i;
            try { return { ...i, metadata_json: JSON.parse(i.metadata_json) }; } catch { return { ...i, metadata_json: {} }; }
        });
    } catch {
        return null;
    }
}

/**
 * Fetch Trending Reports (for SSR)
 */
export async function fetchTrendingReports(params: { pageSize?: number; tag?: string } | number = 10) {
    const queryParams = new URLSearchParams();

    if (typeof params === 'number') {
        queryParams.set("page_size", params.toString());
    } else {
        if (params.pageSize) queryParams.set("page_size", params.pageSize.toString());
        if (params.tag) queryParams.set("tag", params.tag);
    }

    const res = await fetch(
        `${API_BASE}/v1/public/reports?${queryParams.toString()}`,
        {
            cache: 'no-store',
            headers: withTenant()
        }
    );

    if (!res.ok) {
        throw new Error("Failed to fetch trending reports");
    }

    return res.json();
}

/**
 * Send Email via Universal Email Service
 */
export async function sendEmail(payload: {
    form_type: string;
    recipient_email: string;
    recipient_name: string;
    form_data: Record<string, any>;
    attachment?: {
        filename: string;
        content: string; // Base64
    };
}) {
    // Service URL from environment variable
    const EMAIL_SERVICE_URL = API_CONFIG.EMAIL_SERVICE_URL;

    const res = await fetch(EMAIL_SERVICE_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Failed to send email");
    }

    return res.json();
}

// ── Inquiry DB Persistence ───────────────────────────────────────────────────

/**
 * Payload shape mirroring InquiryCreate on the backend.
 */
export interface InquiryPayload {
    form_type: string;
    name: string;
    email: string;
    phone?: string;
    country?: string;
    company?: string;
    message?: string;
    content_id?: string;
    report_name?: string;
    source_url?: string;    // page where the form was submitted (window.location.href)
    referral_url?: string;  // how the user reached that page (document.referrer)
    form_data_json?: Record<string, any>;
}

/**
 * POST /api/v1/public/inquiries
 * Persists a plain-text form submission to the backend DB.
 * Call fire-and-forget — never throws, logs warnings instead.
 */
export async function submitInquiry(payload: InquiryPayload): Promise<void> {
    try {
        const res = await fetch(`${API_BASE}/public/inquiries`, {
            method: "POST",
            headers: withTenant({ "Content-Type": "application/json" }),
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            console.warn("[submitInquiry] DB save failed:", err);
        }
    } catch (e) {
        console.warn("[submitInquiry] Network error:", e);
    }
}

/**
 * POST /api/v1/public/inquiries/with-attachment
 * Uploads form data + a file (≤ 5 MB) to the backend, stores in DB + S3.
 * Returns the attachment_url from the response, or null on failure.
 */
export async function submitInquiryWithAttachment(
    payload: InquiryPayload,
    file: File
): Promise<string | null> {
    try {
        const form = new FormData();

        // Append all inquiry fields as form fields
        form.append("form_type", payload.form_type);
        form.append("name", payload.name);
        form.append("email", payload.email);
        if (payload.phone) form.append("phone", payload.phone);
        if (payload.country) form.append("country", payload.country);
        if (payload.company) form.append("company", payload.company);
        if (payload.message) form.append("message", payload.message);
        if (payload.content_id) form.append("content_id", payload.content_id);
        if (payload.report_name) form.append("report_name", payload.report_name);
        if (payload.source_url) form.append("source_url", payload.source_url);
        if (payload.referral_url) form.append("referral_url", payload.referral_url);
        if (payload.form_data_json) {
            form.append("form_data_json", JSON.stringify(payload.form_data_json));
        }
        form.append("attachment", file);

        const res = await fetch(`${API_BASE}/public/inquiries/with-attachment`, {
            method: "POST",
            headers: withTenant(), // NO Content-Type — browser sets multipart boundary
            body: form,
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            console.warn("[submitInquiryWithAttachment] DB save failed:", err);
            return null;
        }
        const data = await res.json();
        return data?.attachment_url ?? null;
    } catch (e) {
        console.warn("[submitInquiryWithAttachment] Network error:", e);
        return null;
    }
}

/**
 * Fetch Press Releases with filters
 */
export async function fetchPressReleases(params: {
    page?: number;
    page_size?: number;
    press_type?: string;
    q?: string;
    name?: string; // New parameter for title search
}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set("page", params.page.toString());
    if (params.page_size) queryParams.set("page_size", params.page_size.toString());
    if (params.press_type) queryParams.set("press_type", params.press_type);
    if (params.q) queryParams.set("q", params.q);
    if (params.name) queryParams.set("name", params.name);

    const res = await fetch(
        `${API_BASE}/v1/public/press-releases?${queryParams.toString()}`,
        {
            cache: 'no-store',
            headers: withTenant()
        }
    );

    if (!res.ok) {
        throw new Error("Failed to fetch press releases");
    }

    return res.json();
}

/**
 * Fetch Press Release Detail by Slug
 */
export async function fetchPressReleaseBySlug(slug: string) {
    const res = await fetch(
        `${API_BASE}/v1/public/press-releases/${slug}`,
        {
            cache: 'no-store',
            headers: withTenant()
        }
    );

    if (!res.ok) {
        throw new Error("Failed to fetch press release detail");
    }

    return res.json();
}
/**
 * Fetch Blogs with filters
 */
export async function fetchBlogs(params: {
    page?: number;
    page_size?: number;
    q?: string;
    year?: number;
    category?: string;
}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set("page", params.page.toString());
    if (params.page_size) queryParams.set("page_size", params.page_size.toString());
    if (params.q) queryParams.set("q", params.q);
    if (params.year) queryParams.set("year", params.year.toString());
    if (params.category) queryParams.set("category", params.category);

    const res = await fetch(
        `${API_BASE}/v1/public/blogs?${queryParams.toString()}`,
        {
            cache: 'no-store',
            headers: withTenant()
        }
    );

    if (!res.ok) {
        throw new Error("Failed to fetch blogs");
    }

    return res.json();
}

/**
 * Fetch Blog Detail by Slug
 */
export async function fetchBlogBySlug(slug: string) {
    const res = await fetch(
        `${API_BASE}/v1/public/blogs/${slug}`,
        {
            cache: 'no-store',
            headers: withTenant()
        }
    );

    if (!res.ok) {
        throw new Error("Failed to fetch blog detail");
    }

    return res.json();
}

/**
 * Fetch Case Studies with filters
 */
export async function fetchCaseStudies(params: {
    page?: number;
    page_size?: number;
    q?: string;
    name?: string;
    tag?: string;
}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set("page", params.page.toString());
    if (params.page_size) queryParams.set("page_size", params.page_size.toString());
    if (params.q) queryParams.set("q", params.q);
    if (params.name) queryParams.set("name", params.name);
    if (params.tag) queryParams.set("tag", params.tag);

    const res = await fetch(
        `${API_BASE}/v1/public/case-studies?${queryParams.toString()}`,
        {
            cache: 'no-store',
            headers: withTenant()
        }
    );

    if (!res.ok) {
        throw new Error("Failed to fetch case studies");
    }

    return res.json();
}

/**
 * Fetch Case Study Detail by Slug
 */
export async function fetchCaseStudyBySlug(slug: string) {
    const res = await fetch(
        `${API_BASE}/v1/public/case-studies/${slug}`,
        {
            cache: 'no-store',
            headers: withTenant()
        }
    );

    if (!res.ok) {
        throw new Error("Failed to fetch case study detail");
    }

    return res.json();
}

/**
 * Fetch Market Insight Reports with filters
 */
export async function fetchMarketInsightReports(params: {
    page?: number;
    page_size?: number;
    q?: string;
    name?: string;
}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set("page", params.page.toString());
    if (params.page_size) queryParams.set("page_size", params.page_size.toString());
    if (params.q) queryParams.set("q", params.q);
    if (params.name) queryParams.set("search", params.name);

    // Hardcode the report_subtype filter
    queryParams.set("report_subtype", "market_insights");

    const res = await fetch(
        `${API_BASE}/v1/public/reports?${queryParams.toString()}`,
        {
            cache: 'no-store',
            headers: withTenant()
        }
    );

    if (!res.ok) {
        throw new Error("Failed to fetch market insights");
    }

    return res.json();
}

/**
 * Fetch Market Insight Report Detail by Slug
 */
export async function fetchMarketInsightReportBySlug(slug: string) {
    return fetchReportBySlug(slug);
}
